/**
 * Anonymous add-to-cart tracking.
 *
 * Reports every add-to-cart action to the analytics-worker — no phone number
 * or identity required (unlike cart-recovery.js, which only syncs enrolled
 * shoppers). The merchant uses the aggregated counts to decide which
 * products to advertise.
 *
 * Listens for the `cartUpdate` pub/sub event published by product-form.js
 * (source "product-form"), which fires for every product form on the site:
 * product pages, quick view, and sticky add-to-cart. The event's cartData is
 * Shopify's /cart/add.js response describing exactly what was added.
 *
 * Sends via navigator.sendBeacon (plain-text body, so no CORS preflight and
 * it survives page navigation) with a keepalive fetch fallback. Best effort:
 * tracking must never break the shopping UX.
 */
(() => {
	const TRACKING_URL =
		'https://analytics-worker.support-bd5.workers.dev/add-to-cart';

	function extractAddedItems(cartData) {
		if (!cartData || typeof cartData !== 'object') return [];
		// Form POSTs to /cart/add.js return the added line item at the top
		// level; `items` payloads return { items: [...] }. Handle both.
		const raw = Array.isArray(cartData.items) ? cartData.items : [cartData];

		return raw
			.map((item) => ({
				variantId: Number(item.variant_id || item.id),
				productId: Number(item.product_id) || undefined,
				quantity: Number(item.quantity) || 1,
				title: item.product_title || item.title || undefined,
				variantTitle: item.variant_title || undefined,
				price: typeof item.price === 'number' ? item.price : undefined,
			}))
			.filter((item) => Number.isFinite(item.variantId) && item.variantId > 0);
	}

	function track(items) {
		if (!items.length) return;
		const payload = JSON.stringify({ items, source: 'product-form' });

		try {
			// String body → text/plain → simple request (no preflight), and the
			// browser finishes sending it even if the page navigates away.
			if (navigator.sendBeacon && navigator.sendBeacon(TRACKING_URL, payload)) {
				return;
			}
		} catch {
			// fall through to fetch
		}

		fetch(TRACKING_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: payload,
			keepalive: true,
		}).catch(() => {
			// Silently fail — tracking must never break the shopping UX.
		});
	}

	function init() {
		if (window.Shopify?.designMode) return;
		if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined')
			return;

		subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
			if (!event || event.source !== 'product-form' || !event.cartData) return;
			try {
				track(extractAddedItems(event.cartData));
			} catch {
				// Never break the shopping UX.
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
